'use client';

import cn from 'classnames';
import BaseModal from '@/components/modal/lib/BaseModal';
import PriveeKeyholeMaroon from 'public/img/icon/keyhole-red.png';
import Image from 'next/image';
import Text from '@/components/common/Text/Text';
import Button from '@/components/common/Button/Button';
import { useModal } from '@/components/hook/modal';
import { useExpressPriveeInterest } from '@/components/hook/priveeInterest';
import InterestedInPriveeThankYouModal from '@/app/reserve/ExpressedInterestThankYouModal/PriveeExpressedInterestThankYouModal';

import { SMS_OPTIN_COPY } from '@/const/sms_optin_copy';
import analytics from '@/util/analytics';
import { useState } from 'react';
import CheckBox from '@/components/common/CheckBox/CheckBox';

import './PriveeInterestedModal.style.scss';

type PriveeInterestedModalProps = {
  onClose: () => void;
};

function PriveeInterestedModal({ onClose }: PriveeInterestedModalProps) {
  const { showModal } = useModal();
  const { status, submit, isAlreadyInterested } = useExpressPriveeInterest();
  const [smsOptIn, setSmsOptIn] = useState(false);

  const handleInterestedClick = async () => {
    const succeeded = await submit({ smsOptIn });
    if (!succeeded) return;

    showModal(
      <InterestedInPriveeThankYouModal
        onClose={onClose}
        closeButtonText='Back to Vivrelle'
      />
    );
  };

  if (isAlreadyInterested) {
    return <InterestedInPriveeThankYouModal onClose={onClose} />;
  }

  return (
    <BaseModal
      className='privee-interested-modal'
      onClose={onClose}
    >
      <div className='keyhole-img-container'>
        <Image
          src={PriveeKeyholeMaroon}
          alt='Maroon keyhole silhouette'
          width={365}
          height={739}
        />
      </div>
      <Text
        className='privee-interested-title'
        variant='title-xl'
        italic
      >
        Interested in R&eacute;serv&eacute;?
      </Text>
      <Text
        className='privee-interested-description'
        variant='text-m'
      >
        Submit your interest.
      </Text>
      <CheckBox
        className='sms-opt-in'
        checked={smsOptIn}
        onChange={() => {
          const nextValue = !smsOptIn;
          setSmsOptIn(nextValue);
          analytics.track(
            `Privee Express Interest modal (logged in) - SMS Opt-in Clicked`,
            {
              optIn: nextValue
            }
          );
        }}
        label={
          <span className='sms-opt-in-text'>
            {SMS_OPTIN_COPY}{' '}
            <a
              href='/terms'
              className='terms-link'
              target='_blank'
              rel='noopener noreferrer'
            >
              Terms & Conditions.
            </a>
          </span>
        }
      />
      {status === 'error' && (
        <Text
          className='privee-interested-error-text'
          variant='text-s'
        >
          Sorry, something went wrong. Please refresh and try again.
        </Text>
      )}
      <Button
        className={cn('privee-interested-button', {
          'is-submitting': status === 'submitting'
        })}
        variant='dark'
        size='small'
        disabled={status === 'submitting'}
        onClick={handleInterestedClick}
      >
        {status === 'submitting' ? 'Saving\u2026' : "I'M INTERESTED"}
      </Button>
    </BaseModal>
  );
}

export default PriveeInterestedModal;
